import { useState } from 'react';
import { TimesheetIcon } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import type { ReferenceRef } from '@/features/portal/types';
import type { TimesheetDayInput } from '../types';
type DraftDay = TimesheetDayInput;
const WORKED = 'TRAVAILLE';

export const TimesheetDayEditor = ({
  date,
  lines,
  dayTypes,
  workModels,
  onCancel,
  onApply,
  pending,
  error,
  defaultWorkModelId = '',
}: {
  date: string;
  lines: DraftDay[];
  dayTypes: ReferenceRef[];
  workModels: ReferenceRef[];
  onCancel: () => void;
  onApply: (lines: DraftDay[]) => Promise<void>;
  pending: boolean;
  error?: string | undefined;
  defaultWorkModelId?: string;
}) => {
  const worked = dayTypes.find((type) => type.code === WORKED);

  const [first, setFirst] = useState<DraftDay>(
    lines[0] ?? {
      date,
      value: '1',
      dayTypeId: worked?.id ?? dayTypes[0]?.id ?? '',
      workModelId: worked ? defaultWorkModelId || null : null,
    },
  );
  const [second, setSecond] = useState<DraftDay | null>(lines[1] ?? null);

  const isHalf = first.value === '0.5';
  const firstIsWorked = dayTypes.find((type) => type.id === first.dayTypeId)?.code === WORKED;

  const longDate = new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <Modal
      title={longDate}
      description="Renseignez une journée complète ou deux demi-journées."
      icon={<TimesheetIcon />}
      onClose={onCancel}
      closeDisabled={pending}
      footer={
        <div className="cra-day-editor__actions">
          <Button
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={() => {
              void onApply([]).catch(() => {});
            }}
          >
            Effacer ce jour
          </Button>
          <Button size="sm" variant="secondary" disabled={pending} onClick={onCancel}>
            Annuler
          </Button>
          <Button
            size="sm"
            loading={pending}
            loadingLabel="Enregistrement…"
            disabled={pending || !first.dayTypeId || Boolean(second && !second.dayTypeId)}
            onClick={() => {
              void onApply(second ? [first, second] : [first]).catch(() => {});
            }}
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <fieldset className="cra-day-editor" disabled={pending}>
        <legend className="sr-only">Activité de la journée</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label="Nature"
            options={dayTypes.map((type) => ({ value: type.id, label: type.label }))}
            value={first.dayTypeId}
            onChange={(event) => {
              setFirst({ ...first, dayTypeId: event.target.value, workModelId: null });
            }}
          />
          <Select
            label="Durée"
            options={[
              { value: '1', label: 'Journée' },
              { value: '0.5', label: 'Demi-journée' },
            ]}
            value={first.value}
            onChange={(event) => {
              const value = event.target.value;
              setFirst({ ...first, value });
              /* Repasser en journée entière retire la seconde ligne : les deux
               additionnées dépasseraient la journée. */
              if (value === '1') setSecond(null);
            }}
          />
          {/* Sans objet sur un jour non travaillé : on ne télétravaille pas un
            congé, et le serveur le refuse. */}
          {firstIsWorked ? (
            <Select
              label="Lieu"
              placeholder="Non précisé"
              options={workModels.map((model) => ({ value: model.id, label: model.label }))}
              value={first.workModelId ?? ''}
              onChange={(event) => {
                setFirst({ ...first, workModelId: event.target.value || null });
              }}
            />
          ) : null}
        </div>

        {isHalf ? (
          second ? (
            <div className="border-navy-100 grid gap-3 border-t pt-3 sm:grid-cols-3">
              <Select
                label="Seconde demi-journée"
                options={dayTypes.map((type) => ({ value: type.id, label: type.label }))}
                value={second.dayTypeId}
                onChange={(event) => {
                  setSecond({ ...second, dayTypeId: event.target.value, workModelId: null });
                }}
              />
              {dayTypes.find((type) => type.id === second.dayTypeId)?.code === WORKED && (
                <Select
                  label="Lieu de la seconde demi-journée"
                  placeholder="Non précisé"
                  options={workModels.map((model) => ({ value: model.id, label: model.label }))}
                  value={second.workModelId ?? ''}
                  onChange={(event) =>
                    setSecond({ ...second, workModelId: event.target.value || null })
                  }
                />
              )}
              <div className="flex items-end pb-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSecond(null);
                  }}
                >
                  Retirer
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setSecond({
                  date,
                  value: '0.5',
                  dayTypeId:
                    dayTypes.find((type) => type.id !== first.dayTypeId)?.id ?? first.dayTypeId,
                  workModelId: null,
                });
              }}
            >
              Ajouter la seconde demi-journée
            </Button>
          )
        ) : null}
      </fieldset>
      {error && (
        <p role="alert" className="cra-notice">
          {error}
        </p>
      )}
    </Modal>
  );
};
